import React from 'react';
import {ReportFileFiltersType} from "../hooks/useCsReportFileQuery";
import InputFilterItem from "../../../delivery/components/InputFilterItem";
import ParcelFilterStyle from "../../../delivery/components/ParcelFilterStyle";
import DateRangeFilterItem from "../../../delivery/components/DateRangeFilterItem";
import { useTranslation } from 'react-i18next';
import {ReportFileTypeEnum} from "../../../../enums/ReportFileEnum";
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import { isReportDownloadSelfOnlyRole } from '../../../../utils/auth';

interface CsReportFilterProps {
    filters: ReportFileFiltersType;
    setFilters: (filters: ReportFileFiltersType) => void;
    operatorOptions: {value: string, label: string}[];
}

const CsReportFileFilter: React.FC<CsReportFilterProps> = ({
    filters,
    setFilters,
    operatorOptions,
}) => {
    const {t} = useTranslation();
    const operatorLockedToSelf = isReportDownloadSelfOnlyRole();

    const reportTypeOptions = [
        { value: '', label: t('enum.reportTypeOptions.all') },
        { value: '1', label: t('enum.reportTypeOptions.daily') },
        { value: '2', label: t('enum.reportTypeOptions.weekly') },
        { value: '3', label: t('enum.reportTypeOptions.monthly') },
        { value: '4', label: t('enum.reportTypeOptions.parcelDownload') },
        { value: ReportFileTypeEnum.PARCEL_SHIPPING_LABEL_PDF.toString(), label: t('enum.reportTypeOptions.parcelShippingLabelPdf') },
        { value: '6', label: t('enum.reportTypeOptions.pickupAppointmentDownload') },
        { value: ReportFileTypeEnum.POD_REVIEW.toString() , label: t('enum.reportTypeOptions.podReviewDownload') },
        { value: ReportFileTypeEnum.ZIPCODE_EXPORT.toString() , label: t('enum.reportTypeOptions.zipcodeExport') },
        { value: ReportFileTypeEnum.DASHBOARD_EXPORT.toString(), label: t('enum.reportTypeOptions.dashboardExport') },
        { value: ReportFileTypeEnum.VOUCHER_CLEANING.toString(), label: t('enum.reportTypeOptions.voucherCleaning') },
        { value: ReportFileTypeEnum.ACCOUNT_TRANSACTION_FLOW.toString(), label: t('enum.reportTypeOptions.accountTransactionFlow') },
        { value: ReportFileTypeEnum.ACCOUNT_BALANCE_SNAPSHOT.toString(), label: t('enum.reportTypeOptions.accountBalanceSnapshot') },
        { value: ReportFileTypeEnum.TRANSACTION_FLOW_RECORD.toString(), label: t('enum.reportTypeOptions.transactionFlowRecord') },
        { value: ReportFileTypeEnum.REALTIME_SETTLEMENT_ORDER_DETAIL.toString(), label: t('enum.reportTypeOptions.realtimeSettlementOrderDetail') },
        { value: ReportFileTypeEnum.TMS_TASK.toString(), label: t('enum.reportTypeOptions.tmsTaskDownload') },
        { value: ReportFileTypeEnum.EXCEPTION_DOWNLOAD.toString(), label: t('enum.reportTypeOptions.exceptionDownload') },
        { value: ReportFileTypeEnum.CHANNEL_ONBOARDING_DOCUMENT.toString(), label: t('enum.reportTypeOptions.channelOnboardingDocument') },
        { value: ReportFileTypeEnum.DELIVERY_DRIVER_EXPORT.toString(), label: t('enum.reportTypeOptions.deliveryDriverExport') },
        { value: ReportFileTypeEnum.ORDER_CLOSE_TOOL_DOWNLOAD.toString(), label: t('enum.reportTypeOptions.orderCloseToolDownload') },
        { value: ReportFileTypeEnum.DELIVERY_CONFIRM_FEEDBACK.toString(), label: t('enum.reportTypeOptions.deliveryConfirmFeedback') },
        { value: ReportFileTypeEnum.BAG_TAG_EXPORT.toString(), label: t('enum.reportTypeOptions.bagTagExport') },
        { value: ReportFileTypeEnum.WAREHOUSE_LOCATION_EXPORT.toString(), label: t('enum.reportTypeOptions.warehouseLocationExport') },
        { value: ReportFileTypeEnum.DELIVERY_TASK_TRACKING_NUMBER_EXPORT.toString(), label: t('enum.reportTypeOptions.deliveryTaskTrackingNumberExport') },
        { value: ReportFileTypeEnum.DSP_SUMMARY_EXPORT.toString(), label: t('enum.reportTypeOptions.dspSummaryExport') },
        { value: ReportFileTypeEnum.MISSING_UNIT_PENDING.toString(), label: t('enum.reportTypeOptions.missingUnitPending') },
        { value: ReportFileTypeEnum.QC_PENALTY_UPLOAD_RESULT.toString(), label: t('enum.reportTypeOptions.qcPenaltyUploadResult') },
        { value: ReportFileTypeEnum.REVIEW_TASK_EXPORT.toString(), label: t('enum.reportTypeOptions.reviewTaskExport') },
        { value: ReportFileTypeEnum.PRICING_FAIL_DATA_DETAIL.toString(), label: t('enum.reportTypeOptions.pricingFailDataDetail') },
        { value: ReportFileTypeEnum.PRE_BILL_REVISION_TASK_DETAIL.toString(), label: t('enum.reportTypeOptions.preBillRevisionTaskDetail') },
        { value: ReportFileTypeEnum.BILLING_PERIOD_SUMMARY.toString(), label: t('enum.reportTypeOptions.billingPeriodSummary') },
        { value: ReportFileTypeEnum.BILLING_PERIOD_RECEIVE_SUMMARY.toString(), label: t('enum.reportTypeOptions.billingPeriodReceiveSummary') },
        { value: ReportFileTypeEnum.UNDELIVERABLE_UPLOAD_DETAIL.toString(), label: t('enum.reportTypeOptions.undeliverableUploadDetail') },
    ];

    // 处理报表类型变化
    const handleTypeChange = (value: string) => {
        setFilters({...filters, type: value ? parseInt(value) : undefined});
    };

    // 处理操作人变化
    const handleOperatorChange = (value: string) => {
        setFilters({...filters, operatorId: value ? parseInt(value) : undefined});
    };

    // 处理报表名称变化
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilters({...filters, name: e.target.value || undefined});
    };

    // 处理报表变化
    const handleReportDateChange = (
        dates: [string | null, string | null] | null
    ) => {
        setFilters({...filters, reportDate: dates});
    };

    const col1Labels = [t('central.download.filters.reportDate'), t('central.download.filters.operator')];
    const col2Labels = [t('central.download.filters.reportName')];
    const col3Labels = [t('central.download.filters.type')];
    const col1SpanClassName = getSmartLabelClassName(col1Labels);
    const col2SpanClassName = getSmartLabelClassName(col2Labels);
    const col3SpanClassName = getSmartLabelClassName(col3Labels);

    return (
        <div className="mb-2 pl-0 py-2">
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                {/* 第一列 - 统一标签宽度（报表日期4个字，用w-[60px]） */}
                <DateRangeFilterItem
                    label={t('central.download.filters.reportDate')}
                    value={filters.reportDate}
                    onChange={handleReportDateChange}
                    spanClassName={col1SpanClassName}
                />
                {/* 第二列 - 统一标签宽度（报表名称4个字，用w-[60px]） */}
                <InputFilterItem
                    label={t('central.download.filters.reportName')}
                    value={filters.name?.toString() || ''}
                    onChange={handleNameChange}
                    spanClassName={col2SpanClassName}
                />

                {/* 第三列 - 类型2个字，按规范使用w-[36px] */}
                <ParcelFilterStyle
                    label={t('central.download.filters.type')}
                    value={filters.type !== undefined ? filters.type.toString() : ''}
                    onChange={handleTypeChange}
                    options={reportTypeOptions}
                    spanClassName={col3SpanClassName}
                />

                {/* 第一列第二行 - 总部等角色仅本人：禁用下拉，与后端强制 operatorId 一致 */}
                <ParcelFilterStyle
                    label={t('central.download.filters.operator')}
                    value={filters.operatorId?.toString() || ''}
                    onChange={handleOperatorChange}
                    options={operatorOptions}
                    spanClassName={col1SpanClassName}
                    disabled={operatorLockedToSelf}
                    allowClear={!operatorLockedToSelf}
                    showSearch={!operatorLockedToSelf}
                />
            </div>
        </div>
    );
};

export default CsReportFileFilter;